import React, { useState, useEffect } from 'react';
import {
  X, ChevronDown, ChevronUp, Filter,
  Search, MapPin, Building2, DollarSign, Briefcase, Star, Landmark
} from 'lucide-react';
import { SetAsideType } from '../../types';
import { SET_ASIDES } from '../../lib/constants';
import { SetAsideIcon } from '../SetAsideIcon';
import { PIPELINE_STAGES, STAGE_META } from '../../lib/pipeline-service';
import { FacetMultiSelect, FacetOption } from './FacetMultiSelect';
import { fetchAgencyFacets, fetchNaicsFacets } from '../../lib/dashboard-facets';

interface FilterState {
  keywords?: string;
  naicsCode?: string[];
  status?: ('open' | 'pending' | 'closed')[];
  agency?: string[];
  minAmount?: number;
  maxAmount?: number;
  setAsides?: SetAsideType[];
  state?: string;
  page?: number;
  limit?: number;
  sortBy?: 'bidwin_score' | 'due_date' | 'posted_date' | 'funding_amount' | 'agency';
  sortOrder?: 'asc' | 'desc';
  favoritesOnly?: boolean;
  pipelineStage?: 'watching' | 'pursuing' | 'submitted' | 'won' | 'lost' | 'skipped';
}

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterState;
  onFilterChange: (filters: Partial<FilterState>) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
}

// US States
const US_STATES: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'DC': 'District of Columbia',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois',
  'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana',
  'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts', 'MI': 'Michigan',
  'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri', 'MT': 'Montana',
  'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota',
  'OH': 'Ohio', 'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania',
  'RI': 'Rhode Island', 'SC': 'South Carolina', 'SD': 'South Dakota',
  'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont', 'VA': 'Virginia',
  'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
};

// Collapsible section component
function FilterSection({
  title,
  icon,
  children,
  defaultOpen = false,
  badge
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: number;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-bw-border last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-bw-surface-subtle transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-bw-muted">{icon}</span>
          <span className="font-medium text-bw-ink">{title}</span>
          {badge !== undefined && badge > 0 && (
            <span className="px-2 py-0.5 bg-bw-primary-tint text-bw-primary text-xs font-medium rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-bw-muted" />
        ) : (
          <ChevronDown className="h-5 w-5 text-bw-muted" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-1">
          {children}
        </div>
      )}
    </div>
  );
}

export function FilterDrawer({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  onApplyFilters,
  onResetFilters
}: FilterDrawerProps) {
  const [stateSearch, setStateSearch] = useState('');
  const [agencyOptions, setAgencyOptions] = useState<FacetOption[]>([]);
  const [naicsOptions, setNaicsOptions] = useState<FacetOption[]>([]);
  const [facetsLoading, setFacetsLoading] = useState(false);
  const [facetsLoaded, setFacetsLoaded] = useState(false);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Load the live agency + NAICS facets (from the open opportunity pool) the
  // first time the drawer opens, so the smart selectors only offer values that
  // actually return results. Counts come along for ranking/visibility.
  useEffect(() => {
    if (!isOpen || facetsLoaded) return;
    let cancelled = false;
    setFacetsLoading(true);
    Promise.all([fetchAgencyFacets(), fetchNaicsFacets()])
      .then(([agencies, naics]) => {
        if (cancelled) return;
        setAgencyOptions(
          agencies.map((a) => ({ value: a.agency, label: a.agency, count: a.count }))
        );
        setNaicsOptions(
          naics.map((n) => ({
            value: n.code,
            label: n.code,
            secondary: n.title,
            count: n.count,
          }))
        );
        setFacetsLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setFacetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, facetsLoaded]);

  // Handle set-aside toggle
  const handleSetAsideToggle = (setAside: SetAsideType) => {
    const currentSetAsides = filters.setAsides || [];
    const newSetAsides = currentSetAsides.includes(setAside)
      ? currentSetAsides.filter(s => s !== setAside)
      : [...currentSetAsides, setAside];
    onFilterChange({ setAsides: newSetAsides });
  };

  // Handle state select
  const handleStateSelect = (stateCode: string) => {
    onFilterChange({ state: filters.state === stateCode ? undefined : stateCode });
    setStateSearch('');
  };

  // Filter states
  const filteredStates = Object.entries(US_STATES).filter(([code, name]) => {
    if (!stateSearch) return true;
    const search = stateSearch.toLowerCase();
    return code.toLowerCase().includes(search) || name.toLowerCase().includes(search);
  });

  // Count active filters
  const activeFilterCount =
    (filters.setAsides?.length || 0) +
    (filters.naicsCode?.length || 0) +
    (filters.agency?.length || 0) +
    (filters.state ? 1 : 0) +
    (filters.minAmount && filters.minAmount > 0 ? 1 : 0) +
    (filters.maxAmount && filters.maxAmount < 10000000 ? 1 : 0) +
    (filters.favoritesOnly ? 1 : 0) +
    (filters.pipelineStage ? 1 : 0);

  // Handle apply and close
  const handleApply = () => {
    onApplyFilters();
    onClose();
  };

  // Handle reset
  const handleReset = () => {
    onResetFilters();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-bw-surface z-50 shadow-2xl flex flex-col transform transition-transform">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-bw-border bg-bw-surface sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Filter className="h-5 w-5 text-bw-primary" />
            <h2 className="text-lg font-semibold text-bw-ink">Filters</h2>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 bg-bw-primary text-white text-xs font-medium rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bw-surface-subtle rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-bw-muted" />
          </button>
        </div>

        {/* Filter Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Status filter removed: every SAM record is status='open'; active vs expired
              is driven by due date and handled by the "Hide past due" toggle on the
              dashboard, so an Open/Pending/Closed control could never match the data. */}

          {/* Saved & Pipeline Section */}
          <FilterSection
            title="Saved & Pipeline"
            icon={<Star className="h-5 w-5" />}
            defaultOpen={true}
            badge={(filters.favoritesOnly ? 1 : 0) + (filters.pipelineStage ? 1 : 0) || undefined}
          >
            <button
              onClick={() => onFilterChange({ favoritesOnly: !filters.favoritesOnly })}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium border transition-all ${
                filters.favoritesOnly
                  ? 'bg-fit-moderate-bg text-fit-moderate border-amber-300'
                  : 'border-bw-border text-bw-muted hover:bg-bw-surface-subtle'
              }`}
            >
              <Star className="h-4 w-4" fill={filters.favoritesOnly ? 'currentColor' : 'none'} />
              Saved only
            </button>
            <div className="flex flex-wrap gap-2 mt-3">
              {PIPELINE_STAGES.map(s => {
                const active = filters.pipelineStage === s;
                return (
                  <button
                    key={s}
                    onClick={() => onFilterChange({ pipelineStage: active ? undefined : s })}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      active ? STAGE_META[s].pill : 'border-bw-border text-bw-muted hover:bg-bw-surface-subtle'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${STAGE_META[s].dot}`} />
                    {STAGE_META[s].label}
                  </button>
                );
              })}
            </div>
          </FilterSection>

          {/* Set-Asides Section */}
          <FilterSection
            title="Set-Asides"
            icon={<Building2 className="h-5 w-5" />}
            badge={filters.setAsides?.length}
          >
            <div className="grid grid-cols-2 gap-2">
              {Object.values(SET_ASIDES).map(setAside => {
                const isSelected = filters.setAsides?.includes(setAside.type);
                return (
                  <button
                    key={setAside.type}
                    onClick={() => handleSetAsideToggle(setAside.type)}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-sm transition-all ${
                      isSelected
                        ? 'border-servet-blue bg-bw-primary-tint text-bw-primary'
                        : 'border-bw-border text-bw-ink hover:border-bw-border hover:bg-bw-surface-subtle'
                    }`}
                  >
                    <SetAsideIcon type={setAside.type} size={18} />
                    <span className="truncate text-left">{setAside.name}</span>
                  </button>
                );
              })}
            </div>
          </FilterSection>

          {/* Agency Section — searchable, from the live opportunity pool */}
          <FilterSection
            title="Agency"
            icon={<Landmark className="h-5 w-5" />}
            badge={filters.agency?.length}
          >
            <FacetMultiSelect
              options={agencyOptions}
              selected={filters.agency || []}
              onChange={(next) => onFilterChange({ agency: next })}
              loading={facetsLoading}
              placeholder="Search agencies…"
              emptyText="No agencies with open opportunities"
            />
          </FilterSection>

          {/* NAICS Codes Section — smart selector: search by code or industry */}
          <FilterSection
            title="NAICS Codes"
            icon={<Briefcase className="h-5 w-5" />}
            badge={filters.naicsCode?.length}
          >
            <FacetMultiSelect
              options={naicsOptions}
              selected={filters.naicsCode || []}
              onChange={(next) => onFilterChange({ naicsCode: next })}
              loading={facetsLoading}
              placeholder="Search by code or industry (e.g. 541512 or “software”)"
              emptyText="No matching NAICS in the open pool"
            />
          </FilterSection>

          {/* Location Section */}
          <FilterSection
            title="Location"
            icon={<MapPin className="h-5 w-5" />}
            badge={filters.state ? 1 : undefined}
          >
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-bw-muted" />
                <input
                  type="text"
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  placeholder="Search states..."
                  className="w-full pl-9 pr-4 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border border-bw-border rounded-lg">
                {filteredStates.map(([code, name]) => (
                  <button
                    key={code}
                    onClick={() => handleStateSelect(code)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-bw-surface-subtle transition-colors ${
                      filters.state === code ? 'bg-bw-primary-tint text-bw-primary' : 'text-bw-ink'
                    }`}
                  >
                    <span>{name}</span>
                    <span className="text-bw-muted">{code}</span>
                  </button>
                ))}
              </div>
            </div>
          </FilterSection>

          {/* Contract Value Section */}
          <FilterSection
            title="Contract Value"
            icon={<DollarSign className="h-5 w-5" />}
            badge={(filters.minAmount && filters.minAmount > 0) || (filters.maxAmount && filters.maxAmount < 10000000) ? 1 : undefined}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-bw-muted mb-1">Minimum</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-bw-muted text-sm">$</span>
                    <input
                      type="number"
                      value={filters.minAmount || ''}
                      onChange={(e) => onFilterChange({ minAmount: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                      className="w-full pl-7 pr-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-bw-muted mb-1">Maximum</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-bw-muted text-sm">$</span>
                    <input
                      type="number"
                      value={filters.maxAmount || ''}
                      onChange={(e) => onFilterChange({ maxAmount: parseInt(e.target.value) || 10000000 })}
                      placeholder="10,000,000"
                      className="w-full pl-7 pr-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              {/* Quick presets */}
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Under $100K', min: 0, max: 100000 },
                  { label: '$100K - $500K', min: 100000, max: 500000 },
                  { label: '$500K - $1M', min: 500000, max: 1000000 },
                  { label: 'Over $1M', min: 1000000, max: 10000000 },
                ].map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => onFilterChange({ minAmount: preset.min, maxAmount: preset.max })}
                    className="px-3 py-1.5 text-xs font-medium text-bw-muted bg-bw-surface-subtle rounded-full hover:bg-gray-200 transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </FilterSection>
        </div>

        {/* Footer with actions */}
        <div className="p-4 border-t border-bw-border bg-bw-surface sticky bottom-0 safe-area-inset-bottom">
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex-1 px-4 py-3 text-bw-ink font-medium border border-bw-border rounded-xl hover:bg-bw-surface-subtle transition-colors"
            >
              Reset All
            </button>
            <button
              onClick={handleApply}
              className="flex-1 px-4 py-3 bg-bw-primary text-white font-medium rounded-xl hover:bg-servet-dark-blue transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
